import {useMutation} from '@github-ui/react-query'
import {fetchCAPI, FetchCAPIError} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {sendEvent} from '@github-ui/hydro-analytics'

interface CreatePullRequestForTaskParams {
  repoNwo: string
  taskId: string
}

interface CreatePullRequestResponse {
  id: number
  number: number
  repository_id: number
}

async function createPullRequestForTask({
  repoNwo,
  taskId,
}: CreatePullRequestForTaskParams): Promise<CreatePullRequestResponse> {
  const response = await fetchCAPI({
    path: `/agents/repos/${repoNwo}/tasks/${taskId}/pulls`,
    method: 'POST',
    retryOn401: true,
  })

  return response.json()
}

interface UseCreatePullRequestForTaskOptions {
  onSuccess?: (data: CreatePullRequestResponse, variables: CreatePullRequestForTaskParams) => void
  onError?: (error: Error, variables: CreatePullRequestForTaskParams) => void
}

export function useCreatePullRequestForTask({onSuccess, onError}: UseCreatePullRequestForTaskOptions = {}) {
  const mutation = useMutation<CreatePullRequestResponse, Error, CreatePullRequestForTaskParams>({
    mutationKey: ['createPullRequestForTask'],
    mutationFn: async (params: CreatePullRequestForTaskParams) => {
      sendEvent('copilot.agent_session.pr_creation_requested', {
        task_id: params.taskId,
        nwo: params.repoNwo,
      })

      try {
        const response = await createPullRequestForTask(params)

        sendEvent('copilot.agent_session.pr_creation_completed', {
          task_id: params.taskId,
          nwo: params.repoNwo,
          pr_id: response.id,
          pr_number: response.number,
        })

        return response
      } catch (error) {
        const status = error instanceof FetchCAPIError ? error.status : undefined
        sendEvent('copilot.agent_session.pr_creation_failed', {
          task_id: params.taskId,
          nwo: params.repoNwo,
          status,
          error: error instanceof Error ? error.message : String(error),
        })
        throw error
      }
    },
    onSuccess,
    onError,
  })

  return {
    createPullRequest: mutation.mutate,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}
